import type { QueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '#app/constants/queryKeys';

export type OperationsListKind = 'incomes' | 'spendings';

/** Ответ PUT active/type|subcategory: { result: 'success', data: { ids: number[] } } */
export function extractActiveIdsFromUpdateResponse(
  response: { data?: { ids?: unknown } } | null | undefined,
  fallbackActiveId?: number | null
): number[] {
  const raw = response?.data?.ids;
  if (Array.isArray(raw) && raw.length > 0) {
    return raw.map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0);
  }
  if (fallbackActiveId != null && Number.isInteger(fallbackActiveId) && fallbackActiveId > 0) {
    return [fallbackActiveId];
  }
  return [];
}

export type ActiveCategoryPatch = {
  type_id?: number | null;
  custom_type_id?: number | null;
  type_text?: string;
  is_unique?: boolean;
};

export type ActiveSubcategoryPatch = {
  subcategory_id?: number | null;
  subcategory_text?: string;
};

function patchRowsByActiveIds<T extends { id?: number }>(
  list: T[] | undefined,
  activeIds: number[],
  patch: Partial<T>
): T[] | undefined {
  if (!Array.isArray(list) || activeIds.length === 0) return list;
  const idSet = new Set(activeIds);
  return list.map((row) => (row.id != null && idSet.has(row.id) ? { ...row, ...patch } : row));
}

/**
 * Обновляет кэш списков доходов/расходов (useIncomesQuery / useSpendingsQuery) по id активов
 * без полного refetch — после успешного PUT active/type или active/subcategory.
 */
export function patchOperationsListCache(
  queryClient: QueryClient,
  kind: OperationsListKind,
  clientId: number,
  currencyId: number,
  activeIds: number[],
  patch: ActiveCategoryPatch | ActiveSubcategoryPatch
): void {
  if (!activeIds.length) return;

  queryClient.setQueriesData(
    { queryKey: QUERY_KEYS[kind].all(clientId, currencyId), exact: false },
    (old: { id?: number }[] | undefined) => patchRowsByActiveIds(old, activeIds, patch)
  );
}
